// src/screens/PendingOrdersScreen.jsx
import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, storage } from "../firebase";
import {
  doc,
  updateDoc,
  addDoc,
  collection,
  query,
  where,
  limit,
  getDocs,
} from "firebase/firestore";
import { ref as sRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { toast } from "react-toastify";

/** ---------------- UI helpers ---------------- */
const SpinnerRed = ({ size = 48 }) => (
  <div
    style={{ width: size, height: size }}
    className="rounded-full border-4 border-red-200 border-t-red-600 animate-spin"
  />
);

const LoaderRed = ({ text = "Cargando…" }) => (
  <div className="min-h-[40vh] flex flex-col items-center justify-center gap-3">
    <SpinnerRed />
    <div className="text-red-700 font-medium">{text}</div>
  </div>
);

const currency = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 2,
  });

const toMillis = (v) => {
  if (!v) return null;
  if (typeof v?.toDate === "function") return v.toDate().getTime(); // Firestore Timestamp
  if (v instanceof Date) return v.getTime();
  const nn = Number(v);
  if (Number.isFinite(nn) && nn > 0) return nn;
  const d = new Date(v);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
};

const formatDateTime = (v) => {
  const ms = toMillis(v);
  if (!ms) return "—";
  return new Date(ms).toLocaleString("es-AR");
};

/** ---------------- reglas negocio ---------------- */
const MIN_TO_PAY_ARS = 500_000;

/** ---------------- Impuestos/Descuentos ---------------- */
const IVA_PCT = 21;
const PRONTO_PAGO_PCT = 10;

// Lee porcentajes por cliente (default 0)
// Guardalos en pedidosPendientesByClient:
//  - iibbCabaPct: number (ej 3 para 3%)
//  - iibbArbaPct: number (ej 2 para 2%)
const getIibbPctsFromData = (data) => {
  const caba = Number(data?.iibbCabaPct ?? data?.IIBB_CABA_PCT ?? 0);
  const arba = Number(data?.iibbArbaPct ?? data?.IIBB_ARBA_PCT ?? 0);
  return {
    iibbCabaPct: Number.isFinite(caba) ? caba : 0,
    iibbArbaPct: Number.isFinite(arba) ? arba : 0,
  };
};

/** ---------------- Bancos ---------------- */
const BANKS = [
  {
    banco: "BANCO BBVA",
    extra: "SUC 111",
    cta: "CTA CTE: 5165/8",
    cbu: "0170111720000000516589",
    alias: "PUPILA.SALTO.CANADA",
    nota: "(7 CEROS)",
  },
  {
    banco: "BANCO MACRO",
    extra: "CTA CTE",
    cta: "",
    cbu: "2850456230023257031006",
    alias: "PROPIO.ALBUM.SALINA",
  },
  {
    banco: "BANCO SANTANDER RÍO",
    extra: "CTA CTE $ 321-5388/2",
    cta: "",
    cbu: "0720321120000000538822",
    alias: "ASTRO.AFRICA.ALCE",
    nota: "(7 CEROS)",
  },
  {
    banco: "BANCO NACIÓN",
    extra: "CTA CTE 00100010055916",
    cta: "",
    cbu: "0110001320000100559163",
    alias: "INVITO.PIEL.PRUEBA",
  },
];

/** ---------------- Estado pedido + Sync cobro ---------------- */
const pedidoStatus = (p) => {
  if (p?.paid) return "paid";
  if (p?.cancelled) return "cancelled";
  return "pending";
};

const buildCobroResumenFromPedidos = (pedidosArr, pedidoKeyFn) => {
  const paidPedidoKeys = [];
  const cancelledPedidoKeys = [];
  const pendingPedidoKeys = [];

  (Array.isArray(pedidosArr) ? pedidosArr : []).forEach((p, idx) => {
    const key = pedidoKeyFn(p, idx);
    const st = pedidoStatus(p);
    if (st === "paid") paidPedidoKeys.push(key);
    else if (st === "cancelled") cancelledPedidoKeys.push(key);
    else pendingPedidoKeys.push(key);
  });

  const paymentStatus =
    paidPedidoKeys.length > 0
      ? "paid"
      : pendingPedidoKeys.length === 0 && cancelledPedidoKeys.length > 0
      ? "cancelled"
      : "pending";

  return {
    paymentStatus,
    paidPedidoKeys,
    cancelledPedidoKeys,
    pendingPedidoKeys,
  };
};

async function syncCobroDocByToken({
  token,
  sourceDocId,
  pedidosArr,
  pedidoKeyFn,
  extra = {},
}) {
  if (!token) return { ok: false, reason: "missing_token" };

  const q = query(
    collection(db, "clientesPagoParaContactar"),
    where("tokenClient", "==", token),
    limit(1)
  );

  const qs = await getDocs(q);
  if (qs.empty) {
    // No cortamos el flujo principal
    return { ok: false, reason: "not_found" };
  }

  const found = qs.docs[0];
  const resumen = buildCobroResumenFromPedidos(pedidosArr, pedidoKeyFn);

  const now = new Date();

  await updateDoc(doc(db, "clientesPagoParaContactar", found.id), {
    ...resumen,

    // espejo/auditoría
    sourceDocId: String(sourceDocId ?? ""),
    updatedAt: now,

    // regla: si pagó al menos 1 => paid
    paymentStatus: resumen.paymentStatus,

    lastClientActionAt: now,

    ...extra,
  });

  return { ok: true, cobroDocId: found.id, ...resumen };
}

export default function PendingOrdersScreen() {
  const { token } = useParams(); // ✅ viene de /pendingorders/:token
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({}); // { [pedidoKey]: true/false }

  const [showBankInfo, setShowBankInfo] = useState(false);

  // comprobante
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptSent, setReceiptSent] = useState(false);

  // confirm modal cancelar
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [confirmCancelKeys, setConfirmCancelKeys] = useState([]); // pedidoKey[]
  const [confirmCancelMode, setConfirmCancelMode] = useState("multi"); // "one" | "multi"

  const bankSectionRef = useRef(null);

  // ---------------- Stock helpers ----------------
  // "Con stock" = alcanza para cubrir la cantidad pedida
  const hasStock = (it) => {
    if (typeof it?.tieneStockDisponible === "boolean")
      return it.tieneStockDisponible;

    const disponible = Number(it?.estanteriaDisponibleTotal ?? 0);
    const requerido = Number(it?.CANTIDAD_PEDI ?? 0);

    if (!Number.isFinite(disponible) || !Number.isFinite(requerido))
      return false;

    return disponible >= requerido;
  };

  const itemNeto = (it) => {
    if (it?.importeNeto != null) return Number(it.importeNeto || 0);

    const cant = Number(it?.CANTIDAD_PEDI || 0);
    const precio = Number(it?.PRECIO_VTA_PEDI || 0);
    const bonif = Number(it?.BONIF1_PED || 0) / 100;
    const bruto = cant * precio;
    const neto = bruto * (1 - bonif);
    return Number.isFinite(neto) ? neto : 0;
  };

  const itemPrecioUnitarioNeto = (it) => {
    const precio = Number(it?.PRECIO_VTA_PEDI || 0);
    const bonif = Number(it?.BONIF1_PED || 0) / 100;
    const unitNeto = precio * (1 - bonif);
    return Number.isFinite(unitNeto) ? unitNeto : 0;
  };

  const pedidoTotalConStock = (pedido) => {
    const items = Array.isArray(pedido?.items) ? pedido.items : [];
    return items.reduce(
      (acc, it) => (hasStock(it) ? acc + itemNeto(it) : acc),
      0
    );
  };

  const pedidoItemsConStockCount = (pedido) => {
    const items = Array.isArray(pedido?.items) ? pedido.items : [];
    return items.reduce((acc, it) => (hasStock(it) ? acc + 1 : acc), 0);
  };

  const pedidoTieneTotalPagable = (pedido) => {
  return Number(pedidoTotalConStock(pedido) || 0) > 0;
};

  // ---------------- load doc by tokenClient ----------------
  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, "pedidosPendientesByClient"),
          where("tokenClient", "==", token),
          limit(1)
        );

        const qs = await getDocs(q);
        if (qs.empty) {
          toast.error("Link inválido o vencido (cliente/pedidos no encontrados).");
          navigate("/");
          return;
        }

        const found = qs.docs[0];
        const d = { id: found.id, ...found.data() };
        if (!mounted) return;

        // ✅ normalizo status en pedidos (por si hay docs viejos)
        const pedidosNorm = (Array.isArray(d.pedidos) ? d.pedidos : []).map(
          (p) => ({
            ...p,
            status: pedidoStatus(p),
          })
        );

        const d2 = { ...d, pedidos: pedidosNorm };

        setData(d2);

        // default: selecciono todos los pedidos activos (no cancelados y no pagados)
        const base = {};
        pedidosNorm.forEach((p, idx) => {
          const key = pedidoKey(p, idx);
          const isCancelled = Boolean(p?.cancelled);
          const isPaid = Boolean(p?.paid);
      base[key] = !isCancelled && !isPaid && pedidoTieneTotalPagable(p);
        });
        setSelected(base);

        setShowBankInfo(false);
        setReceiptFile(null);
        setReceiptSent(false);
      } catch (err) {
        console.error(err);
        toast.error("Error cargando pedidos");
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [token, navigate]);

  const pedidos = useMemo(
    () => (Array.isArray(data?.pedidos) ? data.pedidos : []),
    [data]
  );

  function pedidoKey(p, idx) {
    const pv = p?.PV_PED != null ? String(p.PV_PED) : "";
    const n = p?.NUMERO_PED != null ? String(p.NUMERO_PED) : "";
    return pv && n ? `${pv}-${n}` : `idx-${idx}`;
  }

const isActivePedido = (p) =>
  !p?.cancelled && !p?.paid && pedidoTieneTotalPagable(p);

  const selectedPedidos = useMemo(() => {
    return pedidos
      .map((p, idx) => ({ p, idx, key: pedidoKey(p, idx) }))
      .filter(({ p, key }) => Boolean(selected[key]) && isActivePedido(p));
  }, [pedidos, selected]);

  const selectedCount = selectedPedidos.length;

  // Subtotal (con stock) => antes era el total
  const subtotalSelected = useMemo(() => {
    return selectedPedidos.reduce(
      (acc, { p }) => acc + pedidoTotalConStock(p),
      0
    );
  }, [selectedPedidos]);

  const totalItemsSelected = useMemo(() => {
    return selectedPedidos.reduce(
      (acc, { p }) => acc + pedidoItemsConStockCount(p),
      0
    );
  }, [selectedPedidos]);

  // % variables por cliente (default 0)
  const { iibbCabaPct, iibbArbaPct } = useMemo(
    () => getIibbPctsFromData(data),
    [data]
  );



// const APPLY_PRONTO_ON = "subtotal_iva"; 
// // opciones:
// // "subtotal_iva"  → descuento sobre (subtotal + IVA)
// // "total"         → descuento sobre (subtotal + IVA + IIBB)

// const paymentBreakdown = useMemo(() => {
//   const subtotal = Number(subtotalSelected || 0);

//   // IIBB (NO forman parte de la base del IVA)
//   const iibbCaba = subtotal * (Number(iibbCabaPct || 0) / 100);
//   const iibbArba = subtotal * (Number(iibbArbaPct || 0) / 100);

//   // ✅ IVA SOLO sobre el subtotal
//   const iva = subtotal * (IVA_PCT / 100);

//   const subtotalMasIva = subtotal + iva;
//   const totalAntesProntoPago = subtotalMasIva + iibbCaba + iibbArba;

//   let baseProntoPago =
//     APPLY_PRONTO_ON === "subtotal_iva"
//       ? subtotalMasIva
//       : totalAntesProntoPago;

//   const prontoPago = baseProntoPago * (PRONTO_PAGO_PCT / 100);

//   const totalFinal = totalAntesProntoPago - prontoPago;

//   const round2 = (n) => Number(Number(n || 0).toFixed(2));

//   return {
//     subtotal: round2(subtotal),

//     iibbCabaPct: round2(iibbCabaPct),
//     iibbArbaPct: round2(iibbArbaPct),
//     iibbCaba: round2(iibbCaba),
//     iibbArba: round2(iibbArba),

//     ivaPct: IVA_PCT,
//     iva: round2(iva),

//     subtotalMasIva: round2(subtotalMasIva),
//     totalAntesProntoPago: round2(totalAntesProntoPago),

//     prontoPagoPct: PRONTO_PAGO_PCT,
//     prontoPago: round2(prontoPago),

//     totalFinal: round2(totalFinal),
//   };
// }, [subtotalSelected, iibbCabaPct, iibbArbaPct]);


const paymentBreakdown = useMemo(() => {
  const subtotal = Number(subtotalSelected || 0);

  // ✅ Todos los impuestos calculados sobre el subtotal con stock
  const iibbCaba = subtotal * (Number(iibbCabaPct || 0) / 100);
  const iibbArba = subtotal * (Number(iibbArbaPct || 0) / 100);
  const iva = subtotal * (IVA_PCT / 100);

  // ✅ Total antes de pronto pago = subtotal + todos los impuestos
  const totalAntesProntoPago = subtotal + iibbCaba + iibbArba + iva;

  // ✅ Pronto pago sobre el total antes de pronto pago
  const prontoPago = totalAntesProntoPago * (PRONTO_PAGO_PCT / 100);

  const totalFinal = totalAntesProntoPago - prontoPago;

  const round2 = (n) => Number(Number(n || 0).toFixed(2));

  return {
    subtotal: round2(subtotal),

    iibbCabaPct: round2(iibbCabaPct),
    iibbArbaPct: round2(iibbArbaPct),

    iibbCaba: round2(iibbCaba),
    iibbArba: round2(iibbArba),

    ivaPct: IVA_PCT,
    iva: round2(iva),

    totalAntesProntoPago: round2(totalAntesProntoPago),

    prontoPagoPct: PRONTO_PAGO_PCT,
    prontoPago: round2(prontoPago),

    totalFinal: round2(totalFinal),
  };
}, [subtotalSelected, iibbCabaPct, iibbArbaPct]);



  const totalFinalToTransfer = paymentBreakdown.totalFinal;

  // ✅ regla: mínimo para proceder (sobre SUBTOTAL)
  const meetsMin = subtotalSelected >= MIN_TO_PAY_ARS;
  const canProceed = selectedCount > 0 && meetsMin;

  const minHint = `Mínimo para proceder al pago (subtotal): ${currency(
    MIN_TO_PAY_ARS
  )}.`;

  const toggleAll = (v) => {
    const next = {};
    pedidos.forEach((p, idx) => {
      const key = pedidoKey(p, idx);
 next[key] = v && isActivePedido(p) && pedidoTieneTotalPagable(p);
    });
    setSelected(next);
  };

  const toggleOne = (key) => {
    setSelected((s) => ({ ...s, [key]: !s[key] }));
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const proceedToPayment = async () => {
    if (selectedCount === 0) {
      toast.error("Seleccioná al menos un pedido para pagar.");
      return;
    }
    if (!meetsMin) {
      toast.error(minHint);
      return;
    }
    setShowBankInfo(true);
    setReceiptSent(false);
    setTimeout(
      () =>
        bankSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        }),
      200
    );
  };

  function calcDocTotals(pedidosArr) {
    // Totales SOLO de activos (no pagados y no cancelados)
    return pedidosArr.reduce(
      (acc, p) => {
        if (!isActivePedido(p)) return acc;
        acc.totalPedidos += 1;
        acc.totalEconomico += Number(p?.totalEconomico || 0);
        acc.totalItemsCantidad += Number(p?.totalItemsCantidad || 0);
        return acc;
      },
      { totalPedidos: 0, totalEconomico: 0, totalItemsCantidad: 0 }
    );
  }

  // ---------------- Cancelación con confirm ----------------
  const openCancelConfirmForSelected = () => {
    const keys = selectedPedidos.map(({ key }) => key);
    if (!keys.length) {
      toast.info("No hay pedidos seleccionados para cancelar.");
      return;
    }
    setConfirmCancelMode("multi");
    setConfirmCancelKeys(keys);
    setConfirmCancelOpen(true);
  };

  const openCancelConfirmForOne = (key) => {
    setConfirmCancelMode("one");
    setConfirmCancelKeys([key]);
    setConfirmCancelOpen(true);
  };

  const confirmCancelNow = async () => {
    if (!data) return;
    const toCancelKeys = Array.isArray(confirmCancelKeys)
      ? confirmCancelKeys
      : [];
    if (!toCancelKeys.length) return;

    try {
      setSaving(true);

      const cancelledAt = new Date();

      const updatedPedidos = pedidos.map((p, idx) => {
        const key = pedidoKey(p, idx);
        if (toCancelKeys.includes(key)) {
          const next = {
            ...p,
            cancelled: true,
            cancelledAt,
            // por seguridad: si cancela, NO está paid
            paid: false,
          };
          return { ...next, status: pedidoStatus(next) };
        }
        return { ...p, status: pedidoStatus(p) };
      });

      const newTotals = calcDocTotals(updatedPedidos);

      await updateDoc(doc(db, "pedidosPendientesByClient", data.id), {
        pedidos: updatedPedidos,
        ...newTotals,
        updatedAt: cancelledAt,
      });

      // ✅ Sync doc espejo en clientesPagoParaContactar
      await syncCobroDocByToken({
        token,
        sourceDocId: data.id,
        pedidosArr: updatedPedidos,
        pedidoKeyFn: pedidoKey,
        extra: {
          lastCancelAt: cancelledAt,
        },
      });

      setData((d) => ({
        ...d,
        pedidos: updatedPedidos,
        ...newTotals,
        updatedAt: cancelledAt,
      }));

      // deselecciono lo cancelado
      setSelected((s) => {
        const next = { ...s };
        toCancelKeys.forEach((k) => (next[k] = false));
        return next;
      });

      // si canceló, oculto pago
      setShowBankInfo(false);
      setReceiptSent(false);
      setReceiptFile(null);

      toast.success("Pedido(s) cancelado(s).");
    } catch (err) {
      console.error(err);
      toast.error("No se pudieron cancelar los pedidos");
    } finally {
      setSaving(false);
      setConfirmCancelOpen(false);
      setConfirmCancelKeys([]);
    }
  };

  // ---------------- Pago y marcado ----------------
  // const handleUploadReceiptAndMarkPaid = async () => {
  //   if (!data) return;

  //   if (!receiptFile) {
  //     toast.error("Seleccioná un archivo");
  //     return;
  //   }

  //   // Reglas para proceder
  //   if (selectedCount === 0) {
  //     toast.error("Seleccioná al menos un pedido para pagar.");
  //     return;
  //   }
  //   if (!meetsMin) {
  //     toast.error(minHint);
  //     return;
  //   }

  //   try {
  //     setUploadingReceipt(true);

  //     // 1) Subo el comprobante a Storage
  //     const tsNow = Date.now();
  //     const paidAt = new Date();

  //     const path = `paymentReceiptsByClient/${data.id}/${tsNow}_${receiptFile.name}`;
  //     const r = sRef(storage, path);
  //     await uploadBytes(r, receiptFile);
  //     const url = await getDownloadURL(r);

  //     // 2) Marco SOLO los pedidos seleccionados como pagados
  //     const selectedKeys = selectedPedidos.map(({ key }) => key);
  //     const paidSelectionGroupId = `${data.id}_${tsNow}`;

  //     // Para lastPayment: guardamos un detalle por pedido
  //     const paidPedidosSummary = [];

  //     const updatedPedidos = pedidos.map((p, idx) => {
  //       const key = pedidoKey(p, idx);

  //       // Si NO está seleccionado, lo dejo igual pero normalizo status
  //       if (!selectedKeys.includes(key)) {
  //         return { ...p, status: pedidoStatus(p) };
  //       }

  //       const items = Array.isArray(p?.items) ? p.items : [];

  //       // Items con stock suficiente
  //       const paidItems = items
  //         .filter((it) => hasStock(it))
  //         .map((it) => ({
  //           CODIGO_ART: it?.CODIGO_ART ?? "",
  //           DESCRIP_ART: it?.DESCRIP_ART ?? "",
  //           CANTIDAD_PEDI: Number(it?.CANTIDAD_PEDI || 0),
  //           PRECIO_VTA_PEDI: Number(it?.PRECIO_VTA_PEDI || 0),
  //           BONIF1_PED: Number(it?.BONIF1_PED || 0),
  //           importeNeto: Number(itemNeto(it) || 0),
  //         }));

  //       // Items sin stock suficiente -> no se cobran
  //       const unpaidItemsNoStock = items
  //         .filter((it) => !hasStock(it))
  //         .map((it) => ({
  //           CODIGO_ART: it?.CODIGO_ART ?? "",
  //           DESCRIP_ART: it?.DESCRIP_ART ?? "",
  //           CANTIDAD_PEDI: Number(it?.CANTIDAD_PEDI || 0),
  //           PRECIO_VTA_PEDI: Number(it?.PRECIO_VTA_PEDI || 0),
  //           BONIF1_PED: Number(it?.BONIF1_PED || 0),
  //         }));

  //       const paidTotalStockOnly = Number(pedidoTotalConStock(p).toFixed(2));

  //       // Guardamos resumen por pedido
  //       paidPedidosSummary.push({
  //         pedidoKey: key,
  //         PV_PED: p?.PV_PED ?? null,
  //         NUMERO_PED: p?.NUMERO_PED ?? null,
  //         subtotalPagadoStockOnly: paidTotalStockOnly,
  //         paidItemsCount: paidItems.length,
  //         unpaidItemsNoStockCount: unpaidItemsNoStock.length,
  //       });

  //       const nextPedido = {
  //         ...p,

  //         // estado pago (solo stock)
  //         paid: true,
  //         paidAt,

  //         paidStockOnly: true,
  //         subtotalPaidStockOnly: paidTotalStockOnly,

  //         // comprobante asociado al grupo
  //         paymentReceiptUrl: url,
  //         paymentReceiptPath: path,
  //         paymentReceiptFile: receiptFile.name,

  //         // agrupador
  //         paidSelectionGroupId,

  //         // detalle
  //         paidItems,
  //         unpaidItemsNoStock,

  //         // monto transferido del grupo (FINAL con impuestos y pronto pago)
  //         finalAmountTransferred: Number(totalFinalToTransfer || 0),
  //         paymentBreakdown,

  //         // por seguridad: si pagó, NO cancelled
  //         cancelled: false,
  //       };

  //       return { ...nextPedido, status: pedidoStatus(nextPedido) };
  //     });

  //     // 3) Recalculo totales del doc (solo activos)
  //     const newTotals = calcDocTotals(updatedPedidos);

  //     // 4) Update del doc principal
  //     await updateDoc(doc(db, "pedidosPendientesByClient", data.id), {
  //       pedidos: updatedPedidos,
  //       ...newTotals,
  //       updatedAt: paidAt,

  //       lastPayment: {
  //         receiptUrl: url,
  //         receiptPath: path,
  //         receiptFile: receiptFile.name,
  //         paidAt,
  //         method: "transfer",
  //         paidSelectionGroupId,

  //         subtotalStockOnly: Number(subtotalSelected || 0),
  //         paidTotalFinal: Number(totalFinalToTransfer || 0),
  //         paymentBreakdown,

  //         paidPedidoKeys: selectedKeys,
  //         paidPedidosSummary,

  //         note:
  //           "El subtotal corresponde únicamente a productos con stock disponible. Luego se aplican IIBB, IVA y descuento por pronto pago.",
  //       },
  //     });

  //     // ✅ Sync doc espejo en clientesPagoParaContactar
  //     await syncCobroDocByToken({
  //       token,
  //       sourceDocId: data.id,
  //       pedidosArr: updatedPedidos,
  //       pedidoKeyFn: pedidoKey,
  //       extra: {
  //         lastPayment: {
  //           receiptUrl: url,
  //           receiptPath: path,
  //           receiptFile: receiptFile.name,
  //           paidAt,
  //           method: "transfer",
  //           paidSelectionGroupId,

  //           subtotalStockOnly: Number(subtotalSelected || 0),
  //           paidTotalFinal: Number(totalFinalToTransfer || 0),
  //           paymentBreakdown,

  //           paidPedidoKeys: selectedKeys,
  //           paidPedidosSummary,
  //           stockOnly: true,
  //         },
  //       },
  //     });




  //           // 5) Armo detalle de productos pagados para pendingOrdersPaid
  //     const paidProductsDetail = [];
  //     const unpaidProductsNoStockDetail = [];

  //     updatedPedidos.forEach((p, idx) => {
  //       const key = pedidoKey(p, idx);
  //       if (!selectedKeys.includes(key)) return;

  //       const paidItems = Array.isArray(p?.paidItems) ? p.paidItems : [];
  //       const unpaidItemsNoStock = Array.isArray(p?.unpaidItemsNoStock)
  //         ? p.unpaidItemsNoStock
  //         : [];

  //       paidItems.forEach((it) => {
  //         const cantidad = Number(it?.CANTIDAD_PEDI || 0);
  //         const precioLista = Number(it?.PRECIO_VTA_PEDI || 0);
  //         const bonificacion = Number(it?.BONIF1_PED || 0);
  //         const subtotal = Number(it?.importeNeto || 0);

  //         paidProductsDetail.push({
  //           pedidoKey: key,
  //           PV_PED: p?.PV_PED ?? null,
  //           NUMERO_PED: p?.NUMERO_PED ?? null,

  //           codigo: it?.CODIGO_ART ?? "",
  //           descripcion: it?.DESCRIP_ART ?? "",
  //           cantidad,
  //           precioUnitario: precioLista,
  //           bonificacion,
  //           subtotal,
  //         });
  //       });

  //       unpaidItemsNoStock.forEach((it) => {
  //         unpaidProductsNoStockDetail.push({
  //           pedidoKey: key,
  //           PV_PED: p?.PV_PED ?? null,
  //           NUMERO_PED: p?.NUMERO_PED ?? null,

  //           codigo: it?.CODIGO_ART ?? "",
  //           descripcion: it?.DESCRIP_ART ?? "",
  //           cantidad: Number(it?.CANTIDAD_PEDI || 0),
  //           precioUnitario: Number(it?.PRECIO_VTA_PEDI || 0),
  //           bonificacion: Number(it?.BONIF1_PED || 0),
  //         });
  //       });
  //     });


  //      const paidPayload = {
  //       createdAt: paidAt,
  //       source: "pedidosPendientesByClient",
  //       sourceDocId: data.id,

  //       ID_CLI: data?.ID_CLI ?? null,
  //       CODIGO_CLI: data?.CODIGO_CLI ?? null,
  //       CUIT_CLI: data?.CUIT_CLI ?? null,
  //       RAZONSOCIAL_CLI: data?.RAZONSOCIAL_CLI ?? null,

  //       paidSelectionGroupId,
  //       paidPedidoKeys: selectedKeys,

  //       totals: {
  //         subtotalStockOnly: Number(subtotalSelected || 0),
  //         totalFinalTransferred: Number(totalFinalToTransfer || 0),
  //         totalPedidos: Number(selectedCount || 0),
  //         totalItemsConStock: Number(totalItemsSelected || 0),
  //       },

  //       paymentBreakdown,
  //       paidPedidosSummary,

  //       // ✅ NUEVO: detalle real de productos pagados
  //       productos: paidProductsDetail,

  //       // opcional: también guardar los que quedaron afuera por falta de stock
  //       unpaidProductosNoStock: unpaidProductsNoStockDetail,

  //       payment: {
  //         receiptUrl: url,
  //         receiptPath: path,
  //         receiptFile: receiptFile.name,
  //         paidAt,
  //         method: "transfer",
  //         stockOnly: true,
  //       },

  //       note:
  //         "El subtotal corresponde únicamente a productos con stock disponible. Luego se aplican IIBB, IVA y descuento por pronto pago.",
  //     };

  //     await addDoc(collection(db, "pendingOrdersPaid"), paidPayload);

  //     // 6) Estado local
  //     setData((d) => ({
  //       ...d,
  //       pedidos: updatedPedidos,
  //       ...newTotals,
  //       updatedAt: paidAt,
  //     }));

  //     // deselecciono lo pagado
  //     setSelected((s) => {
  //       const next = { ...s };
  //       selectedKeys.forEach((k) => (next[k] = false));
  //       return next;
  //     });

  //     setReceiptFile(null);
  //     setReceiptSent(true);

  //     toast.success(
  //       "Comprobante enviado. Se marcaron como pagados los pedidos seleccionados (subtotal con stock + impuestos - pronto pago)."
  //     );
  //   } catch (err) {
  //     console.error(err);
  //     toast.error("No se pudo subir el comprobante / marcar pagos");
  //   } finally {
  //     setUploadingReceipt(false);
  //   }
  // };

    const handleUploadReceiptAndMarkPaid = async () => {
    if (!data) return;

    if (!receiptFile) {
      toast.error("Seleccioná un archivo");
      return;
    }

    // Reglas para proceder
    if (selectedCount === 0) {
      toast.error("Seleccioná al menos un pedido para pagar.");
      return;
    }
    if (!meetsMin) {
      toast.error(minHint);
      return;
    }

    try {
      setUploadingReceipt(true);

      // 1) Subo el comprobante a Storage
      const tsNow = Date.now();
      const paidAt = new Date();

      const path = `paymentReceiptsByClient/${data.id}/${tsNow}_${receiptFile.name}`;
      const r = sRef(storage, path);
      await uploadBytes(r, receiptFile);
      const url = await getDownloadURL(r);

      // 2) Marco SOLO los pedidos seleccionados como pagados
      const selectedKeys = selectedPedidos.map(({ key }) => key);
      const paidSelectionGroupId = `${data.id}_${tsNow}`;

      // Para lastPayment: guardamos un detalle por pedido
      const paidPedidosSummary = [];

      const updatedPedidos = pedidos.map((p, idx) => {
        const key = pedidoKey(p, idx);

        // Si NO está seleccionado, lo dejo igual pero normalizo status
        if (!selectedKeys.includes(key)) {
          return { ...p, status: pedidoStatus(p) };
        }

        const items = Array.isArray(p?.items) ? p.items : [];

        // Items con stock suficiente
        const paidItems = items
          .filter((it) => hasStock(it))
          .map((it) => ({
            CODIGO_ART: it?.CODIGO_ART ?? "",
            DESCRIP_ART: it?.DESCRIP_ART ?? "",
            CANTIDAD_PEDI: Number(it?.CANTIDAD_PEDI || 0),
            PRECIO_VTA_PEDI: Number(it?.PRECIO_VTA_PEDI || 0),
            BONIF1_PED: Number(it?.BONIF1_PED || 0),
            importeNeto: Number(itemNeto(it) || 0),
          }));

        // Items sin stock suficiente -> no se cobran
        const unpaidItemsNoStock = items
          .filter((it) => !hasStock(it))
          .map((it) => ({
            CODIGO_ART: it?.CODIGO_ART ?? "",
            DESCRIP_ART: it?.DESCRIP_ART ?? "",
            CANTIDAD_PEDI: Number(it?.CANTIDAD_PEDI || 0),
            PRECIO_VTA_PEDI: Number(it?.PRECIO_VTA_PEDI || 0),
            BONIF1_PED: Number(it?.BONIF1_PED || 0),
          }));

        const paidTotalStockOnly = Number(pedidoTotalConStock(p).toFixed(2));

        // Guardamos resumen por pedido
        paidPedidosSummary.push({
          pedidoKey: key,
          PV_PED: p?.PV_PED ?? null,
          NUMERO_PED: p?.NUMERO_PED ?? null,
          subtotalPagadoStockOnly: paidTotalStockOnly,
          paidItemsCount: paidItems.length,
          unpaidItemsNoStockCount: unpaidItemsNoStock.length,
        });

        const nextPedido = {
          ...p,

          // estado pago (solo stock)
          paid: true,
          paidAt,

          paidStockOnly: true,
          subtotalPaidStockOnly: paidTotalStockOnly,

          // comprobante asociado al grupo
          paymentReceiptUrl: url,
          paymentReceiptPath: path,
          paymentReceiptFile: receiptFile.name,

          // agrupador
          paidSelectionGroupId,

          // detalle
          paidItems,
          unpaidItemsNoStock,

          // monto transferido del grupo (FINAL con impuestos y pronto pago)
          finalAmountTransferred: Number(totalFinalToTransfer || 0),
          paymentBreakdown,

          // por seguridad: si pagó, NO cancelled
          cancelled: false,
        };

        return { ...nextPedido, status: pedidoStatus(nextPedido) };
      });

      // 3) Recalculo totales del doc (solo activos)
      const newTotals = calcDocTotals(updatedPedidos);

      // 4) Armo detalle de productos pagados para pendingOrdersPaid
      const paidProductsDetail = [];
      const unpaidProductsNoStockDetail = [];

      updatedPedidos.forEach((p, idx) => {
        const key = pedidoKey(p, idx);
        if (!selectedKeys.includes(key)) return;

        const paidItems = Array.isArray(p?.paidItems) ? p.paidItems : [];
        const unpaidItemsNoStock = Array.isArray(p?.unpaidItemsNoStock)
          ? p.unpaidItemsNoStock
          : [];

        paidItems.forEach((it) => {
          const cantidad = Number(it?.CANTIDAD_PEDI || 0);
          const precioLista = Number(it?.PRECIO_VTA_PEDI || 0);
          const bonificacion = Number(it?.BONIF1_PED || 0);
          const subtotal = Number(it?.importeNeto || 0);

          paidProductsDetail.push({
            pedidoKey: key,
            PV_PED: p?.PV_PED ?? null,
            NUMERO_PED: p?.NUMERO_PED ?? null,

            codigo: it?.CODIGO_ART ?? "",
            descripcion: it?.DESCRIP_ART ?? "",
            cantidad,
            precioUnitario: precioLista,
            bonificacion,
            subtotal,
          });
        });

        unpaidItemsNoStock.forEach((it) => {
          unpaidProductsNoStockDetail.push({
            pedidoKey: key,
            PV_PED: p?.PV_PED ?? null,
            NUMERO_PED: p?.NUMERO_PED ?? null,

            codigo: it?.CODIGO_ART ?? "",
            descripcion: it?.DESCRIP_ART ?? "",
            cantidad: Number(it?.CANTIDAD_PEDI || 0),
            precioUnitario: Number(it?.PRECIO_VTA_PEDI || 0),
            bonificacion: Number(it?.BONIF1_PED || 0),
          });
        });
      });

      // 5) Update del doc principal
      await updateDoc(doc(db, "pedidosPendientesByClient", data.id), {
        pedidos: updatedPedidos,
        ...newTotals,
        updatedAt: paidAt,

        lastPayment: {
          receiptUrl: url,
          receiptPath: path,
          receiptFile: receiptFile.name,
          paidAt,
          method: "transfer",
          paidSelectionGroupId,

          subtotalStockOnly: Number(subtotalSelected || 0),
          paidTotalFinal: Number(totalFinalToTransfer || 0),
          paymentBreakdown,

          paidPedidoKeys: selectedKeys,
          paidPedidosSummary,

          note:
            "El subtotal corresponde únicamente a productos con stock disponible. Luego se aplican IIBB, IVA y descuento por pronto pago.",
        },
      });

      // ✅ Sync doc espejo en clientesPagoParaContactar
      await syncCobroDocByToken({
        token,
        sourceDocId: data.id,
        pedidosArr: updatedPedidos,
        pedidoKeyFn: pedidoKey,
        extra: {
          lastPayment: {
            receiptUrl: url,
            receiptPath: path,
            receiptFile: receiptFile.name,
            paidAt,
            method: "transfer",
            paidSelectionGroupId,

            subtotalStockOnly: Number(subtotalSelected || 0),
            paidTotalFinal: Number(totalFinalToTransfer || 0),
            paymentBreakdown,

            paidPedidoKeys: selectedKeys,
            paidPedidosSummary,
            stockOnly: true,
          },
        },
      });

      // 6) Registro en pendingOrdersPaid
      const paidPayload = {
        createdAt: paidAt,
        source: "pedidosPendientesByClient",
        sourceDocId: data.id,

        ID_CLI: data?.ID_CLI ?? null,
        CODIGO_CLI: data?.CODIGO_CLI ?? null,
        CUIT_CLI: data?.CUIT_CLI ?? null,
        RAZONSOCIAL_CLI: data?.RAZONSOCIAL_CLI ?? null,

        paidSelectionGroupId,
        paidPedidoKeys: selectedKeys,

        totals: {
          subtotalStockOnly: Number(subtotalSelected || 0),
          totalFinalTransferred: Number(totalFinalToTransfer || 0),
          totalPedidos: Number(selectedCount || 0),
          totalItemsConStock: Number(totalItemsSelected || 0),
        },

        paymentBreakdown,
        paidPedidosSummary,

        // ✅ NUEVO: detalle real de productos pagados
        productos: paidProductsDetail,

        // ✅ OPCIONAL: auditoría de lo que quedó sin stock
        unpaidProductosNoStock: unpaidProductsNoStockDetail,

        payment: {
          receiptUrl: url,
          receiptPath: path,
          receiptFile: receiptFile.name,
          paidAt,
          method: "transfer",
          stockOnly: true,
        },

        note:
          "El subtotal corresponde únicamente a productos con stock disponible. Luego se aplican IIBB, IVA y descuento por pronto pago.",
      };

      await addDoc(collection(db, "pendingOrdersPaid"), paidPayload);

      // 7) Estado local
      setData((d) => ({
        ...d,
        pedidos: updatedPedidos,
        ...newTotals,
        updatedAt: paidAt,
      }));

      // deselecciono lo pagado
      setSelected((s) => {
        const next = { ...s };
        selectedKeys.forEach((k) => (next[k] = false));
        return next;
      });

      setReceiptFile(null);
      setReceiptSent(true);

      toast.success(
        "Comprobante enviado. Se marcaron como pagados los pedidos seleccionados (subtotal con stock + impuestos - pronto pago)."
      );
    } catch (err) {
      console.error(err);
      toast.error("No se pudo subir el comprobante / marcar pagos");
    } finally {
      setUploadingReceipt(false);
    }
  };

  // ---------------- Responsive: acción sticky mobile ----------------
  const isBusy = saving || uploadingReceipt;

  if (loading) {
    return (
      <div className="p-4 sm:p-6">
        <LoaderRed text="Cargando pedidos…" />
      </div>
    );
  }
  if (!data) return null;

  const clienteNombre = data?.RAZONSOCIAL_CLI || "Cliente";
  const clienteId = data?.ID_CLI ?? data?.CODIGO_CLI ?? data.id;

  return (
<div className="p-4 sm:p-6 max-w-6xl mx-auto pb-56 sm:pb-6">
      {/* Header */}
      <div className="mb-4 sm:mb-5 flex flex-col gap-2">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight">
          Pedidos pendientes de pago
        </h1>
        <div className="text-sm text-gray-600">
          {clienteNombre} · ID: {String(clienteId)}
        </div>
      </div>

      {/* <div className="mb-4 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 shadow-sm">
  <div className="flex items-start gap-3">
    <div className="text-2xl">⚠️</div>
    <div>
      <div className="font-bold text-amber-900">
        Actualización de precios desde el 1 de mayo
      </div>
      <div className="text-sm text-amber-900/90 mt-1">
        Te informamos que a partir del <b>1 de mayo</b> se actualizarán los precios
        de los pedidos que aún no hayan sido abonados. Para mantener los valores
        actuales, es necesario realizar el pago antes de esa fecha.
      </div>
    </div>
  </div>
</div> */}

      {/* Resumen selección */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-5">
        <div className="p-4 rounded-2xl shadow border border-gray-100 bg-white">
          <div className="text-xs text-gray-500">Seleccionados</div>
          <div className="text-2xl font-extrabold">{selectedCount}</div>
          <div className="text-xs text-gray-500">Pedidos</div>
        </div>

        <div className="p-4 rounded-2xl shadow border border-gray-100 bg-white">
          <div className="text-xs text-gray-500">Items</div>
          <div className="text-2xl font-extrabold">{totalItemsSelected}</div>
          <div className="text-xs text-gray-500">Cantidad total</div>
        </div>

        <div className="hidden md:block p-4 rounded-2xl shadow border border-gray-100 bg-white">
          <div className="text-xs text-gray-500">Subtotal</div>
          <div className="text-2xl sm:text-3xl font-extrabold">
            {currency(paymentBreakdown.subtotal)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Total final a transferir: <b>{currency(totalFinalToTransfer)}</b>
          </div>
          {!meetsMin && selectedCount > 0 && (
            <div className="text-xs text-red-600 mt-1">{minHint}</div>
          )}
        </div>
      </div>

      {/* Acciones (desktop) */}
      <div className="hidden sm:flex mb-4 flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="flex flex-wrap gap-2">
          <button
            disabled={isBusy}
            onClick={() => toggleAll(true)}
            className="px-3 py-2 rounded-xl border bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Seleccionar todos
          </button>
          <button
            disabled={isBusy}
            onClick={() => toggleAll(false)}
            className="px-3 py-2 rounded-xl border bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Deseleccionar todos
          </button>

          <button
            disabled={isBusy || selectedCount === 0}
            onClick={openCancelConfirmForSelected}
            className="px-3 py-2 rounded-xl border-2 border-red-500 text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            Cancelar seleccionados
          </button>
        </div>




<div className="flex flex-col items-end gap-1 w-full md:w-auto">
  <button
    disabled={isBusy || !canProceed}
    onClick={proceedToPayment}
    className="w-full md:w-auto px-5 py-3 rounded-2xl shadow-lg hover:shadow-xl font-semibold
      disabled:opacity-50 disabled:cursor-not-allowed bg-red-600 hover:bg-red-700 text-white"
  >
    <div className="flex flex-col items-center md:items-end leading-tight">
      <span>Proceder al pago</span>
      <span className="text-xl sm:text-2xl font-extrabold">
        {currency(totalFinalToTransfer)}
      </span>
      <span className="text-xs text-white/80 text-center md:text-right">
        Total final a transferir (incluye IIBB + IVA - pronto pago)
      </span>
    </div>
  </button>

  <div className="w-full md:max-w-sm mt-3 rounded-xl border-2 border-red-300 bg-red-50 p-3 text-sm text-red-800 text-center font-semibold">
    Importante: aunque envíes el comprobante por WhatsApp, es necesario
    subirlo también en esta web para poder validar el pago y continuar con el proceso.
  </div>

  {!canProceed && selectedCount > 0 && !meetsMin && (
    <div className="text-xs text-red-600 text-center md:text-right w-full">
      {minHint}
    </div>
  )}
</div>



        
      </div>





      {/* Lista de pedidos */}
      <div className="rounded-2xl shadow border border-gray-100 bg-white overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 flex items-center justify-between">
          <div className="font-semibold">Pedidos</div>
          <div className="hidden sm:block text-xs text-gray-600">
            Seleccioná los pedidos que querés pagar.
          </div>
        </div>

        <div className="divide-y">
          {pedidos.map((p, idx) => {
            const key = pedidoKey(p, idx);
            const checked = Boolean(selected[key]) && isActivePedido(p);


            
  // 👇 ACÁ va
  const items = p?.items || [];

  const itemsConStock = items.filter((it) => {
    if (it?.tieneStockDisponible !== undefined) {
      return it.tieneStockDisponible;
    }

    if (it?.ESTANTERIA !== undefined && it?.CANTIDAD_PEDI !== undefined) {
      return Number(it.ESTANTERIA) >= Number(it.CANTIDAD_PEDI);
    }

    return true;
  });

            const totalPagablePedido = pedidoTotalConStock(p);
const pedidoSinStockPagable = totalPagablePedido <= 0;

            return (
              <div key={key} className="p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex items-start gap-3">
              <input
  type="checkbox"
  disabled={isBusy || !isActivePedido(p) || pedidoSinStockPagable}
  checked={checked}
  onChange={() => toggleOne(key)}
  className="mt-1 w-5 h-5"
/>

                    <div className="min-w-0">
                      <div className="font-semibold break-words">
                        Pedido {p?.PV_PED ? `${p.PV_PED}-` : ""}
                        {p?.NUMERO_PED ?? "(s/n)"}
                        {p?.cancelled ? (
                          <span className="ml-2 text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">
                            Cancelado
                          </span>
                        ) : null}
                        {p?.paid ? (
                          <span className="ml-2 text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">
                            Pagado
                          </span>
                        ) : null}
                        {!p?.paid && !p?.cancelled ? (
                          <span className="ml-2 text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                            Pendiente
                          </span>
                        ) : null}

                        {pedidoSinStockPagable && !p?.paid && !p?.cancelled ? (
  <span className="ml-2 text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800">
    Sin stock para pagar
  </span>
) : null}
                      </div>

                      <div className="text-sm text-gray-600 break-words">
                        {p?.DESCRIP_VC ? `Canal: ${p.DESCRIP_VC} · ` : ""}
                        Fecha: {formatDateTime(p?.FECHA_PED)}
                      </div>

                      {/* total en mobile: SUBTOTAL con stock */}
                      <div className="md:hidden mt-2 text-sm">
                        <span className="text-gray-500">
                          Subtotal:
                        </span>{" "}
                        <b>{currency(pedidoTotalConStock(p))}</b>{" "}
                        <span className="text-gray-500">· Items:</span>{" "}
                        <b>{Number(p?.totalItemsCantidad || 0)}</b>
                        <div className="text-xs text-gray-400 mt-1">
                          Total original: {currency(p?.totalEconomico)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* lado derecho (desktop) */}
                  <div className="hidden md:flex flex-col items-end gap-2">
                    <div className="text-right">
                      <div className="text-xs text-gray-500">
                        Subtotal
                      </div>
                      <div className="text-lg font-extrabold">
                        {currency(pedidoTotalConStock(p))}
                      </div>
                      <div className="text-xs text-gray-400">
                        Total original: {currency(p?.totalEconomico)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Items: {Number(p?.totalItemsCantidad || 0)}
                      </div>
                    </div>

                    {!p?.cancelled && !p?.paid && (
                      <button
                        disabled={isBusy}
                        onClick={() => openCancelConfirmForOne(key)}
                        className="px-3 py-2 rounded-xl border-2 border-red-500 text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        Cancelar pedido
                      </button>
                    )}
                  </div>

                  {/* botón cancelar (mobile) */}
                  {!p?.cancelled && !p?.paid && (
                    <div className="md:hidden">
                      <button
                        disabled={isBusy}
                        onClick={() => openCancelConfirmForOne(key)}
                        className="w-full px-3 py-2 rounded-xl border-2 border-red-500 text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        Cancelar pedido
                      </button>
                    </div>
                  )}
                </div>

                {/* Detalle items con stock / sin stock */}
                <details className="mt-3">
                  <summary className="cursor-pointer text-sm text-gray-700">
          Ver detalle de productos ({itemsConStock.length})
                  </summary>

                  {(() => {
                    const items = Array.isArray(p?.items) ? p.items : [];
                    const itemsConStock = items.filter((it) => hasStock(it));
                    const itemsSinStock = items.filter((it) => !hasStock(it));

                    return (
                      <div className="mt-3 space-y-4">
                        {/* Con stock */}
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                          <div className="font-semibold text-emerald-800 text-sm">
                            ✅ Con stock ({itemsConStock.length})
                          </div>

                          {itemsConStock.length === 0 ? (
                            <div className="text-sm text-emerald-900/70 mt-2">
                              No hay productos con stock.
                            </div>
                          ) : (
                            <div className="mt-2 overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-white/60">
                                  <tr>
                                    <th className="text-left p-2">Código</th>
                                    <th className="text-left p-2">
                                      Descripción
                                    </th>
                                    <th className="text-right p-2">Cant.</th>
                                    <th className="text-right p-2">Precio</th>
                                    <th className="text-right p-2">Bonif.</th>
                                    <th className="text-right p-2">
                                      Neto unit.
                                    </th>
                                    <th className="text-right p-2">
                                      Importe neto
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {itemsConStock.map((it, k) => (
                                    <tr
                                      key={`ok-${k}`}
                                      className="border-t border-emerald-100"
                                    >
                                      <td className="p-2">{it?.CODIGO_ART}</td>
                                      <td className="p-2 break-words max-w-[60ch]">
                                        {it?.DESCRIP_ART}
                                      </td>
                                      <td className="p-2 text-right">
                                        {Number(it?.CANTIDAD_PEDI || 0)}
                                      </td>
                                      <td className="p-2 text-right">
                                        {currency(it?.PRECIO_VTA_PEDI)}
                                      </td>
                                      <td className="p-2 text-right">
                                        {Number(it?.BONIF1_PED || 0)}%
                                      </td>
                                      <td className="p-2 text-right">
                                        {currency(itemPrecioUnitarioNeto(it))}
                                      </td>
                                      <td className="p-2 text-right font-semibold">
                                        {currency(itemNeto(it))}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>

                        {/* Sin stock (SIN precios) */}
                        {/* <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                          <div className="font-semibold text-amber-800 text-sm">
                            ⚠️ Sin stock ({itemsSinStock.length})
                          </div>
                          <div className="text-xs text-amber-900/70 mt-1">
                            Estos productos <b>no se incluyen</b> en el subtotal.
                          </div>

                          {itemsSinStock.length === 0 ? (
                            <div className="text-sm text-amber-900/70 mt-2">
                              No hay productos sin stock.
                            </div>
                          ) : (
                            <div className="mt-2 overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-white/60">
                                  <tr>
                                    <th className="text-left p-2">Código</th>
                                    <th className="text-left p-2">
                                      Descripción
                                    </th>
                                    <th className="text-right p-2">Cant.</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {itemsSinStock.map((it, k) => (
                                    <tr
                                      key={`no-${k}`}
                                      className="border-t border-amber-100"
                                    >
                                      <td className="p-2">{it?.CODIGO_ART}</td>
                                      <td className="p-2 break-words max-w-[60ch]">
                                        {it?.DESCRIP_ART}
                                      </td>
                                      <td className="p-2 text-right">
                                        {Number(it?.CANTIDAD_PEDI || 0)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div> */}
                      </div>
                    );
                  })()}
                </details>
              </div>
            );
          })}

          {pedidos.length === 0 && (
            <div className="p-6 text-center text-gray-600">
              No hay pedidos pendientes.
            </div>
          )}
        </div>
      </div>

      {/* Total seleccionado (mobile) */}
      <div className="md:hidden mt-4 p-4 rounded-2xl shadow border border-gray-100 bg-white">
        <div className="text-xs text-gray-500">Total final a transferir</div>
        <div className="text-2xl font-extrabold">
          {currency(totalFinalToTransfer)}
        </div>
        <div className="text-xs text-gray-500 mt-1">
          Subtotal: <b>{currency(paymentBreakdown.subtotal)}</b>
        </div>
        {!meetsMin && selectedCount > 0 && (
          <div className="text-xs text-red-600 mt-1">{minHint}</div>
        )}
      </div>

      {/* Bancos + Subida de comprobante */}
      {showBankInfo && (
        <div
          ref={bankSectionRef}
          className="mt-6 p-4 rounded-2xl border border-emerald-200 bg-emerald-50"
        >
          <div className="mb-2">
            <div className="font-semibold">Cuentas Bancarias</div>
            <div className="text-sm text-gray-700">
              CHARKO S.A. — CUIT: 30-70945753-1 (PHIPHI TOYS)
            </div>

            {/* Total + desglose */}
            <div className="mt-3 rounded-2xl bg-white border border-emerald-200 p-4 shadow-sm">
              <div className="text-xs text-gray-600">Total final a transferir</div>
              <div className="text-3xl sm:text-4xl font-extrabold text-emerald-700">
                {currency(totalFinalToTransfer)}
              </div>
              {/* <div className="text-sm text-gray-700 mt-1">
                <b>Subtotal</b> (solo stock) + <b>IIBB</b> + <b>IVA</b> −{" "}
                <b>10% pronto pago</b>.
              </div> */}

              <div className="mt-3 text-sm text-gray-800 space-y-1">
                <div className="flex justify-between gap-3">
                  <span>Subtotal</span>
                  <b>{currency(paymentBreakdown.subtotal)}</b>
                </div>

                <div className="flex justify-between gap-3">
                  <span>IIBB CABA ({paymentBreakdown.iibbCabaPct}%)</span>
                  <b>{currency(paymentBreakdown.iibbCaba)}</b>
                </div>

                <div className="flex justify-between gap-3">
                  <span>IIBB ARBA ({paymentBreakdown.iibbArbaPct}%)</span>
                  <b>{currency(paymentBreakdown.iibbArba)}</b>
                </div>

                {/* <div className="flex justify-between gap-3">
                  <span>Base</span>
                  <b>{currency(paymentBreakdown.base)}</b>
                </div> */}

                <div className="flex justify-between gap-3">
                  <span>IVA ({paymentBreakdown.ivaPct}%)</span>
                  <b>{currency(paymentBreakdown.iva)}</b>
                </div>

                <div className="flex justify-between gap-3">
                  <span>Total antes pronto pago</span>
                  <b>{currency(paymentBreakdown.totalAntesProntoPago)}</b>
                </div>

                <div className="flex justify-between gap-3 text-emerald-800">
                  <span>Pronto pago (-{paymentBreakdown.prontoPagoPct}%)</span>
                  <b>- {currency(paymentBreakdown.prontoPago)}</b>
                </div>

                <div className="pt-2 border-t flex justify-between gap-3 text-base">
                  <span className="font-semibold">Total final a transferir</span>
                  <span className="font-extrabold text-emerald-700">
                    {currency(paymentBreakdown.totalFinal)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {BANKS.map((b, i) => (
              <div key={i} className="rounded-xl bg-white p-3 border">
                <div className="font-medium">{b.banco}</div>
                {b.extra && <div className="text-xs text-gray-600">{b.extra}</div>}
                {b.cta && <div className="text-xs text-gray-600">{b.cta}</div>}

                <div className="mt-2 text-sm flex items-center justify-between gap-3">
                  <div>
                    CBU: <b className="break-all">{b.cbu}</b>{" "}
                    {b.nota ? <span className="text-gray-500">{b.nota}</span> : null}
                  </div>
                  <button
                    className="px-3 py-1 rounded border"
                    onClick={() => handleCopy(b.cbu)}
                  >
                    Copiar
                  </button>
                </div>

                <div className="mt-1 text-sm flex items-center justify-between gap-3">
                  <div>
                    ALIAS: <b className="break-all">{b.alias}</b>
                  </div>
                  <button
                    className="px-3 py-1 rounded border"
                    onClick={() => handleCopy(b.alias)}
                  >
                    Copiar
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Subida comprobante */}
          <div className="mt-6 rounded-2xl border-4 border-amber-300 bg-amber-50 p-4">
            {!receiptSent ? (
              <>
                <div className="flex items-start gap-3">
                  <div className="text-2xl">🧾</div>
                  <div>
                    <div className="text-lg font-bold">
                      Subí el comprobante de <u>transferencia</u>
                    </div>
                    <div className="text-sm text-gray-700">
                      Se marcarán como <b>pagados</b> únicamente los pedidos seleccionados.
                    </div>

                    <div className="mt-3 rounded-xl border-2 border-red-300 bg-red-50 p-3 text-sm text-red-800 font-semibold">
  Importante: aunque envíes el comprobante por WhatsApp, es necesario subirlo
  también en esta web para que podamos continuar correctamente con el proceso
  de pago y preparación del pedido.
</div>
                  </div>
                </div>

                <div className="mt-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <input
                    id="receipt"
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                    className="block text-sm"
                  />

                  <button
                    disabled={!receiptFile || uploadingReceipt || !canProceed}
                    onClick={handleUploadReceiptAndMarkPaid}
                    className="px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 w-full sm:w-auto"
                  >
                    {uploadingReceipt ? "Subiendo…" : "Enviar comprobante"}
                  </button>
                </div>

                {selectedCount > 0 && !meetsMin && (
                  <div className="mt-2 text-sm text-red-700">{minHint}</div>
                )}
              </>
            ) : (
              <div className="flex items-start gap-3">
                <div className="text-2xl">✅</div>
                <div>
                  <div className="text-lg font-bold">¡Comprobante enviado!</div>
                  <div className="text-sm text-gray-700">
                    Se marcaron como <b>pagados</b> los pedidos seleccionados.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

{/* Barra sticky mobile (acciones) */}
<div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t p-3">
  <div className="flex gap-2">
    <button
      disabled={isBusy || selectedCount === 0}
      onClick={openCancelConfirmForSelected}
      className="flex-1 px-3 py-3 rounded-2xl border-2 border-red-500 text-red-600 disabled:opacity-50"
    >
      Cancelar
    </button>

    <button
      disabled={isBusy || !canProceed}
      onClick={proceedToPayment}
      className="flex-1 px-3 py-3 rounded-2xl bg-red-600 text-white font-semibold disabled:opacity-50"
    >
      <div className="flex flex-col leading-tight">
        <span>Pagar</span>
        <span className="text-lg font-extrabold">
          {currency(totalFinalToTransfer)}
        </span>
      </div>
    </button>
  </div>

  <div className="mt-3 rounded-xl border-2 border-red-300 bg-red-50 p-2 text-xs text-red-800 text-center font-semibold">
    Importante: aunque envíes el comprobante por WhatsApp, es necesario subirlo también en esta web.
  </div>

  {selectedCount > 0 && !meetsMin && (
    <div className="mt-2 text-xs text-red-600">{minHint}</div>
  )}

  <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
    <button
      disabled={isBusy}
      onClick={() => toggleAll(true)}
      className="underline disabled:opacity-50"
    >
      Seleccionar todos
    </button>
    <button
      disabled={isBusy}
      onClick={() => toggleAll(false)}
      className="underline disabled:opacity-50"
    >
      Deseleccionar
    </button>
  </div>
</div>

      {/* Modal confirmación cancelar */}
      <Modal
        open={confirmCancelOpen}
        onClose={() => setConfirmCancelOpen(false)}
        title="Confirmar cancelación"
      >
        <div className="p-3 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">
          {confirmCancelMode === "one" ? (
            <>
              ¿Seguro que querés <b>cancelar este pedido</b>? Esta acción no se
              puede deshacer desde esta pantalla.
            </>
          ) : (
            <>
              ¿Seguro que querés <b>cancelar los pedidos seleccionados</b>? Esta
              acción no se puede deshacer desde esta pantalla.
            </>
          )}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            disabled={isBusy}
            onClick={() => setConfirmCancelOpen(false)}
            className="px-4 py-2 rounded-xl border bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Volver
          </button>
          <button
            disabled={isBusy}
            onClick={confirmCancelNow}
            className="px-4 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            Sí, cancelar
          </button>
        </div>
      </Modal>

      {/* Overlay */}
      {(saving || uploadingReceipt) && (
        <div className="fixed inset-0 z-[70] bg-black/30 backdrop-blur-[1px] flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl px-6 py-5 flex flex-col items-center gap-3">
            <SpinnerRed />
            <div className="text-sm text-red-700 font-medium">
              {uploadingReceipt ? "Subiendo comprobante…" : "Guardando…"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** ---------------- Modal ---------------- */
function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-gray-100">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <h3 className="text-lg font-semibold">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <div className="px-5 py-4">{children}</div>
        </div>
      </div>
    </div>
  );
}